import { useLocation, Link } from "wouter";
import {
  BarChart3,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  MessageSquare,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

declare const __APP_VERSION__: string;
const APP_VERSION = __APP_VERSION__;
import { ROUTES } from "@shared/constants";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard", href: ROUTES.dashboard, icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: "Explorer", href: ROUTES.explorer, icon: <Search className="h-5 w-5" /> },
  {
    label: "Chat",
    href: ROUTES.chat,
    icon: <MessageSquare className="h-5 w-5" />,
  },
  { label: "Analytics", href: ROUTES.analytics, icon: <BarChart3 className="h-5 w-5" /> },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const [location] = useLocation();

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="flex h-14 items-center justify-center gap-2.5 border-b border-sidebar-border px-4">
        {!collapsed ? (
          <>
            <BrainCircuit className="h-5 w-5 text-primary emboss-icon" />
            <span
              className="text-lg tracking-widest text-sidebar-foreground"
              style={{ fontFamily: "var(--font-brand)", fontWeight: 600 }}
            >
              MENTOR
            </span>
          </>
        ) : (
          <BrainCircuit className="mx-auto h-6 w-6 text-primary emboss-icon" />
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 p-2" aria-label="Menu principal">
        {NAV_ITEMS.map((item) => {
          const isActive = location === item.href;
          const linkContent = (
            <span
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "border-l-2 border-primary bg-primary/10 text-primary"
                  : "text-sidebar-foreground hover:bg-muted",
                item.disabled && "pointer-events-none opacity-40",
                collapsed && "justify-center border-l-0 px-0",
              )}
            >
              {item.icon}
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && item.disabled && (
                <span className="ml-auto text-xs text-muted-foreground">Em breve</span>
              )}
            </span>
          );

          if (item.disabled) {
            return collapsed ? (
              <Tooltip key={item.href}>
                <TooltipTrigger asChild>
                  <div>{linkContent}</div>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label} (em breve)</TooltipContent>
              </Tooltip>
            ) : (
              <div key={item.href}>{linkContent}</div>
            );
          }

          return collapsed ? (
            <Tooltip key={item.href}>
              <TooltipTrigger asChild>
                <Link href={item.href}>{linkContent}</Link>
              </TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          ) : (
            <Link key={item.href} href={item.href}>
              {linkContent}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-sidebar-border p-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-sidebar-foreground"
          aria-label={collapsed ? "Expandir sidebar" : "Recolher sidebar"}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
        {!collapsed && (
          <p className="mt-1 text-center text-xs text-muted-foreground">v{APP_VERSION}</p>
        )}
      </div>
    </aside>
  );
}
